import { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import Swal from 'sweetalert2';
import {
  useCreateFieldMutation,
  useUpdateFieldMutation,
  useGetAvailableSourceFieldsQuery,
  useGetFieldTypesQuery,
  useGetMetadataSchemaQuery,
} from '../../../Services/PathwayAdminAPI';
import '../../../Styles/FieldConfigurator.css';

const FieldConfigurator = ({ show, onHide, exerciseId, field, onSuccess, currentStepNumber }) => {
  const [fieldType, setFieldType] = useState('text');
  const [componentType, setComponentType] = useState('text-input');
  const [label, setLabel] = useState('');
  const [variableName, setVariableName] = useState('');
  const [placeholder, setPlaceholder] = useState('');
  const [isRequired, setIsRequired] = useState(false);
  const [options, setOptions] = useState([{ value: '', label: '', description: '' }]);
  const [validationRules, setValidationRules] = useState({
    maxLength: '',
    minLength: '',
    rows: 3,
  });
  
  // Display-only specific fields
  const [sourceStep, setSourceStep] = useState('');
  const [sourcePhase, setSourcePhase] = useState('');
  const [sourceVariableName, setSourceVariableName] = useState('');

  // Formula mode for calculated display-only fields
  const [isFormulaMode, setIsFormulaMode] = useState(false);
  const [formulaExpression, setFormulaExpression] = useState('');
  const [formulaFormat, setFormulaFormat] = useState('number');
  
  // Help text (shown below the field label on the user-facing form)
  const [helpText, setHelpText] = useState('');
  
  // Component metadata for all field types
  const [componentMetadata, setComponentMetadata] = useState({});
  
  // Repeater fields
  const [repeaterFields, setRepeaterFields] = useState([]);
  
  const [errors, setErrors] = useState({});

  const [createField, { isLoading: isCreating }] = useCreateFieldMutation();
  const [updateField, { isLoading: isUpdating }] = useUpdateFieldMutation();
  const isLoading = isCreating || isUpdating;

  // Fetch available field types
  const { data: fieldTypesData, isLoading: isLoadingFieldTypes, error: fieldTypesError } = useGetFieldTypesQuery(undefined, { skip: !show });

  // Debug logging
  useEffect(() => {
    if (show) {
      console.log('Field Types Data:', fieldTypesData);
      console.log('Is Loading:', isLoadingFieldTypes);
      console.log('Error:', fieldTypesError);
    }
  }, [show, fieldTypesData, isLoadingFieldTypes, fieldTypesError]);

  // Fetch available source fields for display-only fields
  const { data: availableSourceFields } = useGetAvailableSourceFieldsQuery(
    { before_step_number: currentStepNumber },
    { skip: !show || (fieldType !== 'display' && componentType !== 'list-view' && componentType !== 'connect-list') }
  );

  // Fetch metadata schema for current component type
  const { data: metadataSchema } = useGetMetadataSchemaQuery(
    componentType,
    { skip: !show || !componentType }
  );

  // Component type mappings (legacy support)
  const componentTypeMappings = {
    'text': 'text-input',
    'textarea': 'text-area',
    'radio': 'radio-group',
    'checkbox': 'checkbox-single',
    'dropdown': 'dropdown',
    'display': 'display-only',
    'button': 'button',
    'file': 'file-upload',
    'repeater': 'repeater',
    'list': 'list-view',
  };

  useEffect(() => {
    if (show && field) {
      // Edit mode - populate form with existing field data
      setFieldType(field.field_type || 'text');
      setComponentType(field.component_type || componentTypeMappings[field.field_type] || 'text-input');
      setLabel(field.label || '');
      setVariableName(field.variable_name || '');
      setPlaceholder(field.placeholder || '');
      setIsRequired(field.is_required || false);

      // Set source step and variable name for display-only fields
      if (field.source_step) {
        setSourceStep(field.source_step);
      }
      // Check for source_phase in component_metadata
      let parsedMeta = field.component_metadata;
      if (typeof parsedMeta === 'string') {
        try { parsedMeta = JSON.parse(parsedMeta); } catch (e) { parsedMeta = {}; }
      }
      if (parsedMeta?.source_phase) {
        setSourcePhase(parsedMeta.source_phase);
      }
      if (field.field_type === 'display' && field.variable_name) {
        setSourceVariableName(field.variable_name);
      }

      // Detect formula mode from component_metadata
      let parsedMetaForFormula = field.component_metadata;
      if (typeof parsedMetaForFormula === 'string') {
        try { parsedMetaForFormula = JSON.parse(parsedMetaForFormula); } catch (e) { parsedMetaForFormula = null; }
      }
      if (parsedMetaForFormula?.source_step === 'calculated' && parsedMetaForFormula?.formula) {
        setIsFormulaMode(true);
        setFormulaExpression(parsedMetaForFormula.formula);
        setFormulaFormat(parsedMetaForFormula.format || 'number');
      } else {
        setIsFormulaMode(false);
        setFormulaExpression('');
        setFormulaFormat('number');
      }

      // Parse options if it's a JSON string
      let parsedOptions = field.options;
      if (typeof field.options === 'string') {
        try {
          parsedOptions = JSON.parse(field.options);
        } catch (e) {
          console.error('Failed to parse options:', e);
          parsedOptions = [];
        }
      }

      if (parsedOptions && Array.isArray(parsedOptions) && parsedOptions.length > 0) {
        const normalizedOptions = parsedOptions.map(opt => {
          if (typeof opt === 'string') {
            return { value: opt, label: opt, description: '' };
          }
          return opt;
        });
        setOptions(normalizedOptions);
      } else {
        setOptions([{ value: '', label: '', description: '' }]);
      }

      // Parse validation_rules if it's a JSON string
      let parsedValidationRules = field.validation_rules;
      if (typeof field.validation_rules === 'string') {
        try {
          parsedValidationRules = JSON.parse(field.validation_rules);
        } catch (e) {
          console.error('Failed to parse validation_rules:', e);
          parsedValidationRules = null;
        }
      }

      if (parsedValidationRules) {
        setValidationRules({
          maxLength: parsedValidationRules.maxLength || '',
          minLength: parsedValidationRules.minLength || '',
          rows: parsedValidationRules.rows || 3,
        });
      }

      // Parse component_metadata if it's a JSON string
      let parsedMetadata = field.component_metadata;
      if (typeof field.component_metadata === 'string') {
        try {
          parsedMetadata = JSON.parse(field.component_metadata);
        } catch (e) {
          console.error('Failed to parse component_metadata:', e);
          parsedMetadata = null;
        }
      }

      if (parsedMetadata) {
        // Merge defaults so required fields (e.g. date_format) are always present
        const componentTypeForDefaults = field.component_type || componentTypeMappings[field.field_type] || 'text-input';
        const merged = { ...getDefaultMetadata(componentTypeForDefaults), ...parsedMetadata };
        setComponentMetadata(merged);
        
        // Load help_text from metadata
        if (parsedMetadata.help_text) {
          setHelpText(parsedMetadata.help_text);
        }
        
        // Load repeater fields if present — deep-copy to avoid mutating frozen RTK Query cache
        if (parsedMetadata.fields) {
          setRepeaterFields(parsedMetadata.fields.map(f => ({ ...f })));
        }
      } else {
        // No existing metadata — seed defaults for the component type
        const componentTypeForDefaults = field.component_type || componentTypeMappings[field.field_type] || 'text-input';
        setComponentMetadata(getDefaultMetadata(componentTypeForDefaults));
      }
    } else if (show && !field) {
      resetForm();
    }
  }, [show, field]);

  const resetForm = () => {
    setFieldType('text');
    setComponentType('text-input');
    setLabel('');
    setVariableName('');
    setPlaceholder('');
    setIsRequired(false);
    setOptions([{ value: '', label: '', description: '' }]);
    setValidationRules({
      maxLength: '',
      minLength: '',
      rows: 3,
    });
    setSourceStep('');
    setSourcePhase('');
    setSourceVariableName('');
    setIsFormulaMode(false);
    setFormulaExpression('');
    setFormulaFormat('number');
    setHelpText('');
    setComponentMetadata({});
    setRepeaterFields([]);
    setErrors({});
  };

  const handleFieldTypeChange = (type) => {
    setFieldType(type);
    setComponentType(componentTypeMappings[type] || type);
    setErrors({});
    
    // Reset type-specific fields
    if (type === 'text' || type === 'textarea') {
      setOptions([{ value: '', label: '', description: '' }]);
      setSourceStep('');
      setSourcePhase('');
      setSourceVariableName('');
      setIsFormulaMode(false);
      setFormulaExpression('');
      setFormulaFormat('number');
    } else if (type === 'radio' || type === 'dropdown' || type === 'checkbox') {
      setPlaceholder('');
      setSourceStep('');
      setSourcePhase('');
      setSourceVariableName('');
      setIsFormulaMode(false);
      setFormulaExpression('');
      setFormulaFormat('number');
    } else if (type === 'display') {
      setPlaceholder('');
      setOptions([{ value: '', label: '', description: '' }]);
    }
  };

  const handleAddOption = () => {
    setOptions([...options, { value: '', label: '', description: '' }]);
  };

  const handleRemoveOption = (index) => {
    if (options.length > 1) {
      const newOptions = options.filter((_, i) => i !== index);
      setOptions(newOptions);
    }
  };

  const handleOptionChange = (index, field, value) => {
    const newOptions = options.map((opt, i) => {
      if (i === index) {
        return { ...opt, [field]: value || '' };
      }
      return opt;
    });
    setOptions(newOptions);
  };

  const handleSourceFieldSelect = (e) => {
    const selectedValue = e.target.value;
    if (!selectedValue) {
      setSourceStep('');
      setSourcePhase('');
      setSourceVariableName('');
      return;
    }

    const [stepNum, varName, phaseName] = selectedValue.split('|');
    setSourceStep(stepNum);
    setSourceVariableName(varName);
    
    // Extract phase number from phase name (e.g., "Phase 1: Concept & Feasibility" -> 1)
    if (phaseName) {
      const phaseMatch = phaseName.match(/Phase (\d+)/i);
      if (phaseMatch) {
        setSourcePhase(parseInt(phaseMatch[1]));
      }
    }
    
    // Auto-populate variable name if empty
    if (!variableName) {
      setVariableName(varName);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!label.trim() && componentType !== 'static-content') {
      newErrors.label = 'Field label is required';
    }

    // Display-only validation (skip for static-content which has no source, and skip for formula mode)
    if (fieldType === 'display' && componentType !== 'static-content' && !isFormulaMode) {
      if (!sourceStep) {
        newErrors.sourceStep = 'Source step is required for display-only fields';
      }
      if (!sourceVariableName) {
        newErrors.sourceVariableName = 'Source variable is required for display-only fields';
      }
      if (!variableName) {
        newErrors.variableName = 'Variable name is required for display-only fields';
      }
    }

    // Formula mode validation
    if (fieldType === 'display' && componentType !== 'static-content' && isFormulaMode) {
      if (!formulaExpression.trim()) {
        newErrors.formula = 'Formula expression is required';
      }
      if (!variableName.trim()) {
        newErrors.variableName = 'Variable name is required for calculated fields';
      }
    }

    if (fieldType === 'radio' || fieldType === 'dropdown' || fieldType === 'checkbox') {
      const validOptions = options.filter(opt =>
        opt && opt.value && opt.value.trim() && opt.label && opt.label.trim()
      );
      if (validOptions.length < 2) {
        newErrors.options = 'At least 2 valid options are required (with both value and label)';
      }

      const values = validOptions.map(opt => opt.value.trim());
      const uniqueValues = new Set(values);
      if (values.length !== uniqueValues.size) {
        newErrors.options = 'Option values must be unique';
      }
    }

    if (fieldType === 'textarea') {
      if (validationRules.rows && (validationRules.rows < 1 || validationRules.rows > 20)) {
        newErrors.rows = 'Rows must be between 1 and 20';
      }
    }

    if (validationRules.maxLength && validationRules.maxLength < 1) {
      newErrors.maxLength = 'Max length must be greater than 0';
    }

    if (validationRules.minLength && validationRules.minLength < 0) {
      newErrors.minLength = 'Min length cannot be negative';
    }

    if (validationRules.minLength && validationRules.maxLength &&
      parseInt(validationRules.minLength) > parseInt(validationRules.maxLength)) {
      newErrors.minLength = 'Min length cannot be greater than max length';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const fieldData = {
        exercise_id: exerciseId,
        field_type: fieldType,
        component_type: componentType,
        label: componentType === 'static-content' ? (label.trim() || '') : label.trim(),
        is_required: isRequired,
        order_index: field ? field.order_index : 0,
      };

      // Add variable name if provided
      if (variableName.trim()) {
        fieldData.variable_name = variableName.trim();
      }

      // Add placeholder if provided
      if (placeholder.trim()) {
        fieldData.placeholder = placeholder.trim();
      }

      // Display-only specific fields (not static-content)
      if ((fieldType === 'display' || componentType === 'display-only') && componentType !== 'static-content') {
        if (isFormulaMode) {
          // Formula / calculated field — no source_step column, metadata carries everything
          fieldData.variable_name = variableName.trim();
          fieldData.component_metadata = {
            source_step: 'calculated',
            formula: formulaExpression.trim(),
            format: formulaFormat,
          };
        } else {
          fieldData.source_step = parseInt(sourceStep);
          fieldData.variable_name = sourceVariableName;
          
          // Add source_phase to component_metadata if specified
          if (sourcePhase) {
            fieldData.component_metadata = {
              source_phase: parseInt(sourcePhase),
            };
          }
        }
      }

      // Add component metadata for new field types
      // Skip if already set by formula mode (display-only calculated fields)
      if (!fieldData.component_metadata) {
        if (componentType === 'repeater') {
          // Always include component_metadata for repeater fields so sub-fields are saved
          fieldData.component_metadata = {
            ...componentMetadata,
            fields: repeaterFields
          };
        } else if (componentType === 'static-content') {
          // Static content needs style + content body
          fieldData.component_metadata = {
            style: componentMetadata.style || 'info',
            ...(componentMetadata.content ? { content: componentMetadata.content } : {}),
          };
        } else if (Object.keys(componentMetadata).length > 0 && componentType !== 'radio-group') {
          fieldData.component_metadata = componentMetadata;
        }
      }

      // Merge help_text into component_metadata for all non-display field types
      if (helpText.trim() && fieldType !== 'display') {
        fieldData.component_metadata = {
          ...(fieldData.component_metadata || {}),
          help_text: helpText.trim(),
        };
      } else if (fieldData.component_metadata) {
        // Remove help_text if cleared
        const { help_text, ...rest } = fieldData.component_metadata;
        fieldData.component_metadata = Object.keys(rest).length > 0 ? rest : fieldData.component_metadata;
      }

      // Add type-specific data (legacy support)
      if (fieldType === 'text' || fieldType === 'textarea') {
        const rules = {};
        if (validationRules.maxLength) {
          rules.maxLength = parseInt(validationRules.maxLength);
        }
        if (validationRules.minLength) {
          rules.minLength = parseInt(validationRules.minLength);
        }
        if (fieldType === 'textarea' && validationRules.rows) {
          rules.rows = parseInt(validationRules.rows);
        }

        if (Object.keys(rules).length > 0) {
          fieldData.validation_rules = rules;
        }
      } else if (fieldType === 'radio' || fieldType === 'dropdown' || fieldType === 'checkbox') {
        const validOptions = options.filter(opt =>
          opt && opt.value && opt.value.trim() && opt.label && opt.label.trim()
        );
        if (validOptions.length > 0) {
          // Transform options to match seeder format (array of objects with value and label)
          fieldData.options = validOptions.map(opt => ({
            value: opt.value.trim(),
            label: opt.label.trim()
          }));
        }
      }

      let response;
      if (field) {
        response = await updateField({
          id: field.id,
          ...fieldData,
        }).unwrap();
      } else {
        response = await createField(fieldData).unwrap();
      }

      Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: response.message || `Field ${field ? 'updated' : 'created'} successfully`,
        timer: 2000,
        showConfirmButton: false,
        confirmButtonColor: '#FD9C00',
      });

      if (onSuccess) {
        onSuccess(response.data);
      }

      onHide();
      resetForm();
    } catch (error) {
      console.error('Error saving field:', error);
      
      let errorMessage = `Failed to ${field ? 'update' : 'create'} field`;
      
      if (error.data?.errors) {
        const errorMessages = Object.values(error.data.errors).flat();
        errorMessage = errorMessages.join(', ');
      } else if (error.data?.message) {
        errorMessage = error.data.message;
      }

      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: errorMessage,
        confirmButtonColor: '#FD9C00',
      });
    }
  };

  const getDefaultMetadata = (type) => {
    switch (type) {
      case 'date-input':
        return { format: 'date', date_format: 'YYYY-MM-DD' };
      case 'currency-input':
        return { format: 'currency', currency_symbol: '$', decimal_places: 2 };
      case 'copy-block':
        return { button_type: 'copy' };
      case 'link-button':
        return { button_type: 'link' };
      case 'download-button':
        return { button_type: 'download' };
      case 'button':
        return { button_type: 'action' };
      case 'static-content':
        return { style: 'info', content: '' };
      default:
        return {};
    }
  };

  const renderComponentTypeSelector = () => {
    if (!fieldTypesData?.data?.grouped) return null;

    return (
      <div className="form-group">
        <label>Component Type <span className="required">*</span></label>
        <select
          value={componentType}
          onChange={(e) => {
            const newType = e.target.value;
            setComponentType(newType);
            // Find the base_type for this component type from the grouped data
            for (const types of Object.values(fieldTypesData.data.grouped)) {
              const match = types.find(t => t.component_type === newType);
              if (match) {
                setFieldType(match.base_type);
                break;
              }
            }
            // Seed required metadata defaults when switching component type
            setComponentMetadata(getDefaultMetadata(newType));
          }}
          className="form-control"
        >
          {Object.entries(fieldTypesData.data.grouped).map(([category, types]) => (
            <optgroup key={category} label={category}>
              {types.map((type) => (
                <option key={type.component_type} value={type.component_type}>
                  {type.display_label}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
        <small className="form-text">Select the specific component variant</small>
      </div>
    );
  };

  const renderRepeaterFieldBuilder = () => {
    return (
      <div className="repeater-builder">
        <div className="form-group">
          <label>Minimum Items</label>
          <input
            type="number"
            min="0"
            value={componentMetadata.min_items || ''}
            onChange={(e) => setComponentMetadata({ ...componentMetadata, min_items: parseInt(e.target.value) || 0 })}
            placeholder="0"
          />
        </div>
        <div className="form-group">
          <label>Maximum Items</label>
          <input
            type="number"
            min="1"
            value={componentMetadata.max_items || ''}
            onChange={(e) => setComponentMetadata({ ...componentMetadata, max_items: parseInt(e.target.value) || null })}
            placeholder="Unlimited"
          />
        </div>
        <div className="form-group">
          <label>Add Button Label</label>
          <input
            type="text"
            value={componentMetadata.add_button_label || ''}
            onChange={(e) => setComponentMetadata({ ...componentMetadata, add_button_label: e.target.value })}
            placeholder="+ Add Item"
          />
        </div>
        <div className="repeater-fields-section">
          <div className="section-header">
            <label>Repeater Fields</label>
            <button
              type="button"
              className="btn-add-option"
              onClick={() => {
                const newField = {
                  type: 'text-input',
                  variable_name: '',
                  label: '',
                  placeholder: '',
                  is_required: false
                };
                setRepeaterFields([...repeaterFields, newField]);
              }}
            >
              <i className="fas fa-plus"></i> Add Field
            </button>
          </div>
          {repeaterFields.map((field, index) => (
            <div key={index} className="repeater-field-item">
              <div className="field-header">
                <span>Field {index + 1}</span>
                <button
                  type="button"
                  className="btn-remove-option"
                  onClick={() => {
                    setRepeaterFields(repeaterFields.filter((_, i) => i !== index));
                  }}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <div className="form-group">
                <label>Field Type</label>
                <select
                  value={field.type}
                  onChange={(e) => {
                    setRepeaterFields(repeaterFields.map((f, i) =>
                      i === index ? { ...f, type: e.target.value } : f
                    ));
                  }}
                >
                  <option value="text-input">Text Input</option>
                  <option value="text-area">Text Area</option>
                  <option value="currency-input">Currency Input</option>
                  <option value="date-input">Date Input</option>
                  <option value="dropdown">Dropdown</option>
                  <option value="checkbox-single">Checkbox</option>
                </select>
              </div>
              <div className="form-group">
                <label>Variable Name <span className="required">*</span></label>
                <input
                  type="text"
                  value={field.variable_name}
                  onChange={(e) => {
                    setRepeaterFields(repeaterFields.map((f, i) =>
                      i === index ? { ...f, variable_name: e.target.value } : f
                    ));
                  }}
                  placeholder="e.g., item_name"
                />
              </div>
              <div className="form-group">
                <label>Label <span className="required">*</span></label>
                <input
                  type="text"
                  value={field.label}
                  onChange={(e) => {
                    setRepeaterFields(repeaterFields.map((f, i) =>
                      i === index ? { ...f, label: e.target.value } : f
                    ));
                  }}
                  placeholder="e.g., Item Name"
                />
              </div>
              <div className="form-group">
                <label>Placeholder</label>
                <input
                  type="text"
                  value={field.placeholder || ''}
                  onChange={(e) => {
                    setRepeaterFields(repeaterFields.map((f, i) =>
                      i === index ? { ...f, placeholder: e.target.value } : f
                    ));
                  }}
                  placeholder="Optional"
                />
              </div>
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={field.is_required || false}
                    onChange={(e) => {
                      setRepeaterFields(repeaterFields.map((f, i) =>
                        i === index ? { ...f, is_required: e.target.checked } : f
                      ));
                    }}
                  />
                  <span>Required</span>
                </label>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderFileUploadConfiguration = () => {
    return (
      <div className="file-upload-config">
        <div className="form-group">
          <label>Accepted File Types</label>
          <input
            type="text"
            value={componentMetadata.accepted_types?.join(', ') || ''}
            onChange={(e) => {
              const types = e.target.value.split(',').map(t => t.trim()).filter(t => t);
              setComponentMetadata({ ...componentMetadata, accepted_types: types });
            }}
            placeholder="e.g., image/jpeg, image/png, application/pdf"
          />
          <small className="form-text">Comma-separated MIME types</small>
        </div>
        <div className="form-group">
          <label>Maximum File Size (MB)</label>
          <input
            type="number"
            min="0.1"
            step="0.1"
            value={componentMetadata.max_file_size ? componentMetadata.max_file_size / 1048576 : ''}
            onChange={(e) => {
              const sizeInBytes = parseFloat(e.target.value) * 1048576;
              setComponentMetadata({ ...componentMetadata, max_file_size: sizeInBytes });
            }}
            placeholder="5"
          />
        </div>
        <div className="form-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={componentMetadata.multiple || false}
              onChange={(e) => setComponentMetadata({ ...componentMetadata, multiple: e.target.checked })}
            />
            <span>Allow multiple files</span>
          </label>
        </div>
      </div>
    );
  };

  const renderButtonConfiguration = () => {
    return (
      <div className="button-config">
        <div className="form-group">
          <label>Button Type <span className="required">*</span></label>
          <select
            value={componentMetadata.button_type || 'link'}
            onChange={(e) => setComponentMetadata({ ...componentMetadata, button_type: e.target.value })}
          >
            <option value="link">Link</option>
            <option value="download">Download</option>
            <option value="copy">Copy to Clipboard</option>
            <option value="action">Action</option>
          </select>
        </div>
        
        {componentMetadata.button_type === 'link' && (
          <div className="form-group">
            <label>External URL <span className="required">*</span></label>
            <input
              type="url"
              value={componentMetadata.external_url || ''}
              onChange={(e) => setComponentMetadata({ ...componentMetadata, external_url: e.target.value })}
              placeholder="https://example.com"
            />
          </div>
        )}
        
        {componentMetadata.button_type === 'download' && (
          <div className="form-group">
            <label>File URL <span className="required">*</span></label>
            <input
              type="text"
              value={componentMetadata.file_url || ''}
              onChange={(e) => setComponentMetadata({ ...componentMetadata, file_url: e.target.value })}
              placeholder="/downloads/file.pdf"
            />
          </div>
        )}
        
        {componentMetadata.button_type === 'copy' && (
          <div className="form-group">
            <label>Content to Copy <span className="required">*</span></label>
            <textarea
              value={componentMetadata.copy_content || ''}
              onChange={(e) => setComponentMetadata({ ...componentMetadata, copy_content: e.target.value })}
              placeholder="Text to copy to clipboard"
              rows={3}
            />
          </div>
        )}
        
        {componentMetadata.button_type === 'action' && (
          <div className="form-group">
            <label>Action Name</label>
            <input
              type="text"
              value={componentMetadata.action_name || componentMetadata.action || ''}
              onChange={(e) => setComponentMetadata({ ...componentMetadata, action_name: e.target.value, action: e.target.value })}
              placeholder="e.g., send_partner_introductions"
            />
          </div>
        )}

        <div className="form-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={componentMetadata.disabled || false}
              onChange={(e) => setComponentMetadata({ ...componentMetadata, disabled: e.target.checked })}
            />
            <span>Disabled (Coming soon)</span>
          </label>
          <small className="form-text">When enabled, the button shows as "Coming soon" and cannot be clicked</small>
        </div>
      </div>
    );
  };

  const renderTextFormatConfiguration = () => {
    return (
      <div className="text-format-config">
        {/* Only show format selector for generic text-input, not date-input or currency-input */}
        {componentType === 'text-input' && (
          <div className="form-group">
            <label>Format Type</label>
            <select
              value={componentMetadata.format || 'text'}
              onChange={(e) => setComponentMetadata({ ...componentMetadata, format: e.target.value })}
            >
              <option value="text">Standard Text</option>
              <option value="currency">Currency</option>
              <option value="date">Date</option>
              <option value="email">Email</option>
              <option value="url">URL</option>
              <option value="number">Number</option>
            </select>
          </div>
        )}
        
        {componentMetadata.format === 'currency' && (
          <>
            <div className="form-group">
              <label>Currency Symbol</label>
              <input
                type="text"
                value={componentMetadata.currency_symbol || '$'}
                onChange={(e) => setComponentMetadata({ ...componentMetadata, currency_symbol: e.target.value })}
                placeholder="$"
              />
            </div>
            <div className="form-group">
              <label>Decimal Places</label>
              <input
                type="number"
                min="0"
                max="4"
                value={componentMetadata.decimal_places || 2}
                onChange={(e) => setComponentMetadata({ ...componentMetadata, decimal_places: parseInt(e.target.value) })}
              />
            </div>
          </>
        )}
        
        {componentMetadata.format === 'date' && (
          <>
            <div className="form-group">
              <label>Date Format</label>
              <input
                type="text"
                value={componentMetadata.date_format || 'YYYY-MM-DD'}
                onChange={(e) => setComponentMetadata({ ...componentMetadata, date_format: e.target.value })}
                placeholder="YYYY-MM-DD"
              />
            </div>
            <div className="form-group">
              <label>Minimum Date</label>
              <input
                type="date"
                value={componentMetadata.min_date || ''}
                onChange={(e) => setComponentMetadata({ ...componentMetadata, min_date: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Maximum Date</label>
              <input
                type="date"
                value={componentMetadata.max_date || ''}
                onChange={(e) => setComponentMetadata({ ...componentMetadata, max_date: e.target.value })}
              />
            </div>
          </>
        )}
      </div>
    );
  };

  const renderCheckboxConfiguration = () => {
    console.log('renderCheckboxConfiguration called', componentMetadata);
    return (
      <div className="checkbox-config">
        <div className="form-group">
          <label>Selection Mode</label>
          <select
            value={componentMetadata.selection_mode || 'multiple'}
            onChange={(e) => setComponentMetadata({ ...componentMetadata, selection_mode: e.target.value })}
          >
            <option value="single">Single Selection</option>
            <option value="multiple">Multiple Selection</option>
            <option value="limited">Limited Selection</option>
          </select>
        </div>
        
        <div className="form-group">
          <label>Minimum Selection</label>
          <input
            type="number"
            min="0"
            value={componentMetadata.min_selections || ''}
            onChange={(e) => setComponentMetadata({ ...componentMetadata, min_selections: e.target.value ? parseInt(e.target.value) : null })}
            placeholder="No minimum (optional)"
            className="form-control"
          />
          <small className="form-text">Minimum number of options that must be selected</small>
        </div>
        
        <div className="form-group">
          <label>Maximum Selection</label>
          <input
            type="number"
            min="1"
            value={componentMetadata.max_selections || ''}
            onChange={(e) => setComponentMetadata({ ...componentMetadata, max_selections: e.target.value ? parseInt(e.target.value) : null })}
            placeholder="No maximum (optional)"
            className="form-control"
          />
          <small className="form-text">Maximum number of options that can be selected</small>
        </div>
        
        {componentMetadata.selection_mode === 'limited' && (
          <div className="form-group">
            <label>Maximum Selections (Legacy)</label>
            <input
              type="number"
              min="1"
              value={componentMetadata.max_selections || ''}
              onChange={(e) => setComponentMetadata({ ...componentMetadata, max_selections: parseInt(e.target.value) })}
              placeholder="e.g., 3"
              className="form-control"
            />
            <small className="form-text">Use "Maximum Selection" above instead</small>
          </div>
        )}
      </div>
    );
  };

  const renderListViewConfiguration = () => {
    return (
      <div className="list-view-config">
        <div className="form-group">
          <label>Data Source Field <span className="required">*</span></label>
          <select
            value={componentMetadata.data_source || ''}
            onChange={(e) => {
              const [stepNum, varName] = e.target.value.split('|');
              setComponentMetadata({
                ...componentMetadata,
                data_source: varName,
                source_step: parseInt(stepNum)
              });
            }}
          >
            <option value="">Select a field from a previous step...</option>
            {availableSourceFields?.data?.grouped_by_step?.map((stepGroup) => (
              <optgroup key={stepGroup.step_number} label={`Step ${stepGroup.step_number}: ${stepGroup.step_title}`}>
                {stepGroup.fields.map((field) => (
                  <option 
                    key={field.field_id} 
                    value={`${field.step_number}|${field.variable_name}`}
                  >
                    {field.field_label} ({field.variable_name})
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label>Item Template</label>
          <input
            type="text"
            value={componentMetadata.item_template || ''}
            onChange={(e) => setComponentMetadata({ ...componentMetadata, item_template: e.target.value })}
            placeholder="e.g., {name} - {value}"
          />
          <small className="form-text">Use {'{field_name}'} for dynamic values</small>
        </div>
        <div className="form-group">
          <label>Empty Message</label>
          <input
            type="text"
            value={componentMetadata.empty_message || ''}
            onChange={(e) => setComponentMetadata({ ...componentMetadata, empty_message: e.target.value })}
            placeholder="No items found"
          />
        </div>
      </div>
    );
  };

  const renderConnectListConfiguration = () => {
    return (
      <div className="connect-list-config">
        <div className="form-group">
          <label>Source Steps <span className="required">*</span></label>
          <small className="form-text">Select multiple steps that contain partner data</small>
          {availableSourceFields?.data?.grouped_by_step?.map((stepGroup) => (
            <label key={stepGroup.step_number} className="checkbox-label">
              <input
                type="checkbox"
                checked={componentMetadata.source_steps?.includes(stepGroup.step_number) || false}
                onChange={(e) => {
                  const currentSteps = componentMetadata.source_steps || [];
                  const newSteps = e.target.checked
                    ? [...currentSteps, stepGroup.step_number]
                    : currentSteps.filter(s => s !== stepGroup.step_number);
                  setComponentMetadata({ ...componentMetadata, source_steps: newSteps });
                }}
              />
              <span>Step {stepGroup.step_number}: {stepGroup.step_title}</span>
            </label>
          ))}
        </div>
        <div className="form-group">
          <label>Empty Message</label>
          <input
            type="text"
            value={componentMetadata.empty_message || ''}
            onChange={(e) => setComponentMetadata({ ...componentMetadata, empty_message: e.target.value })}
            placeholder="No partners selected"
          />
        </div>
      </div>
    );
  };

  const renderFieldTypeOptions = () => {
    // Handle legacy field types
    switch (fieldType) {
      case 'text':
        return (
          <div className="field-type-options">
            <div className="form-group">
              <label>Variable Name</label>
              <input
                type="text"
                value={variableName}
                onChange={(e) => setVariableName(e.target.value)}
                placeholder="e.g., user_core_values (optional, for referencing later)"
              />
              <small className="form-text">Used to reference this field's value in later steps</small>
            </div>
            <div className="form-group">
              <label>Placeholder Text</label>
              <input
                type="text"
                value={placeholder}
                onChange={(e) => setPlaceholder(e.target.value)}
                placeholder="Enter placeholder text (optional)"
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Min Length</label>
                <input
                  type="number"
                  value={validationRules.minLength}
                  onChange={(e) => setValidationRules({ ...validationRules, minLength: e.target.value })}
                  placeholder="Optional"
                  min="0"
                />
                {errors.minLength && <p className="error-text">{errors.minLength}</p>}
              </div>
              <div className="form-group">
                <label>Max Length</label>
                <input
                  type="number"
                  value={validationRules.maxLength}
                  onChange={(e) => setValidationRules({ ...validationRules, maxLength: e.target.value })}
                  placeholder="Optional"
                  min="1"
                />
                {errors.maxLength && <p className="error-text">{errors.maxLength}</p>}
              </div>
            </div>
          </div>
        );

      case 'textarea':
        return (
          <div className="field-type-options">
            <div className="form-group">
              <label>Variable Name</label>
              <input
                type="text"
                value={variableName}
                onChange={(e) => setVariableName(e.target.value)}
                placeholder="e.g., business_description (optional, for referencing later)"
              />
              <small className="form-text">Used to reference this field's value in later steps</small>
            </div>
            <div className="form-group">
              <label>Placeholder Text</label>
              <input
                type="text"
                value={placeholder}
                onChange={(e) => setPlaceholder(e.target.value)}
                placeholder="Enter placeholder text (optional)"
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Number of Rows</label>
                <input
                  type="number"
                  value={validationRules.rows}
                  onChange={(e) => setValidationRules({ ...validationRules, rows: e.target.value })}
                  placeholder="3"
                  min="1"
                  max="20"
                />
                {errors.rows && <p className="error-text">{errors.rows}</p>}
              </div>
              <div className="form-group">
                <label>Character Limit</label>
                <input
                  type="number"
                  value={validationRules.maxLength}
                  onChange={(e) => setValidationRules({ ...validationRules, maxLength: e.target.value })}
                  placeholder="Optional"
                  min="1"
                />
                {errors.maxLength && <p className="error-text">{errors.maxLength}</p>}
              </div>
            </div>
          </div>
        );

      case 'checkbox':
        return (
          <div className="field-type-options">
            <div className="form-group">
              <label>Variable Name</label>
              <input
                type="text"
                value={variableName}
                onChange={(e) => setVariableName(e.target.value)}
                placeholder="e.g., accepts_terms (optional, for referencing later)"
              />
              <small className="form-text">Used to reference this field's value in later steps</small>
            </div>
            
            {/* Min/Max Selection Configuration */}
            <div className="form-group">
              <label>Minimum Selection</label>
              <input
                type="number"
                min="0"
                value={componentMetadata.min_selections || ''}
                onChange={(e) => setComponentMetadata({ ...componentMetadata, min_selections: e.target.value ? parseInt(e.target.value) : null })}
                placeholder="No minimum (optional)"
                className="form-control"
              />
              <small className="form-text">Minimum number of options that must be selected</small>
            </div>
            
            <div className="form-group">
              <label>Maximum Selection</label>
              <input
                type="number"
                min="1"
                value={componentMetadata.max_selections || ''}
                onChange={(e) => setComponentMetadata({ ...componentMetadata, max_selections: e.target.value ? parseInt(e.target.value) : null })}
                placeholder="No maximum (optional)"
                className="form-control"
              />
              <small className="form-text">Maximum number of options that can be selected</small>
            </div>
            
            <div className="options-header">
              <label>Options <span className="required">*</span></label>
              <button type="button" className="btn-add-option" onClick={handleAddOption}>
                <i className="fas fa-plus"></i> Add Option
              </button>
            </div>
            {errors.options && <p className="error-text">{errors.options}</p>}
            <div className="options-list">
              {options.map((option, index) => (
                <div key={index} className="option-item">
                  <div className="option-header">
                    <span className="option-number">Option {index + 1}</span>
                    {options.length > 1 && (
                      <button
                        type="button"
                        className="btn-remove-option"
                        onClick={() => handleRemoveOption(index)}
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    )}
                  </div>
                  <div className="form-group">
                    <label>Value <span className="required">*</span></label>
                    <input
                      type="text"
                      value={option.value || ''}
                      onChange={(e) => handleOptionChange(index, 'value', e.target.value)}
                      placeholder="e.g., option_1"
                    />
                  </div>
                  <div className="form-group">
                    <label>Label <span className="required">*</span></label>
                    <input
                      type="text"
                      value={option.label || ''}
                      onChange={(e) => handleOptionChange(index, 'label', e.target.value)}
                      placeholder="e.g., Option 1"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'radio':
      case 'dropdown':
        return (
          <div className="field-type-options">
            <div className="form-group">
              <label>Variable Name</label>
              <input
                type="text"
                value={variableName}
                onChange={(e) => setVariableName(e.target.value)}
                placeholder="e.g., business_type (optional, for referencing later)"
              />
              <small className="form-text">Used to reference this field's value in later steps</small>
            </div>
            <div className="options-header">
              <label>Options <span className="required">*</span></label>
              <button type="button" className="btn-add-option" onClick={handleAddOption}>
                <i className="fas fa-plus"></i> Add Option
              </button>
            </div>
            {errors.options && <p className="error-text">{errors.options}</p>}
            <div className="options-list">
              {options.map((option, index) => (
                <div key={index} className="option-item">
                  <div className="option-header">
                    <span className="option-number">Option {index + 1}</span>
                    {options.length > 1 && (
                      <button
                        type="button"
                        className="btn-remove-option"
                        onClick={() => handleRemoveOption(index)}
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    )}
                  </div>
                  <div className="form-group">
                    <label>Value <span className="required">*</span></label>
                    <input
                      type="text"
                      value={option.value || ''}
                      onChange={(e) => handleOptionChange(index, 'value', e.target.value)}
                      placeholder="e.g., speed_convenience"
                    />
                  </div>
                  <div className="form-group">
                    <label>Label <span className="required">*</span></label>
                    <input
                      type="text"
                      value={option.label || ''}
                      onChange={(e) => handleOptionChange(index, 'label', e.target.value)}
                      placeholder="e.g., Speed & Convenience"
                    />
                  </div>
                  <div className="form-group">
                    <label>Description</label>
                    <textarea
                      value={option.description || ''}
                      onChange={(e) => handleOptionChange(index, 'description', e.target.value)}
                      placeholder="Optional description for this option"
                      rows={2}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'display':
        // Static content has base type 'display' but needs its own config in the inner switch
        if (componentType !== 'static-content') {
          return (
            <div className="field-type-options">
              <div className="alert alert-info">
                <i className="fas fa-info-circle"></i> Display-only fields show values entered by the user in previous steps, or calculated results.
              </div>

              {/* Formula / Source toggle */}
              <div className="form-group">
                <label>Display Mode</label>
                <div className="btn-group w-100" role="group">
                  <button
                    type="button"
                    className={`btn btn-sm ${!isFormulaMode ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => { setIsFormulaMode(false); setErrors({}); }}
                  >
                    Source Field
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm ${isFormulaMode ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => { setIsFormulaMode(true); setErrors({}); }}
                  >
                    Calculated / Formula
                  </button>
                </div>
              </div>

              {!isFormulaMode ? (
                <>
                  <div className="form-group">
                    <label>Source Field <span className="required">*</span></label>
                    <select
                      value={sourceStep && sourceVariableName ? `${sourceStep}|${sourceVariableName}` : ''}
                      onChange={handleSourceFieldSelect}
                      className={errors.sourceStep || errors.sourceVariableName ? 'is-invalid' : ''}
                    >
                      <option value="">Select a field from a previous step...</option>
                      {availableSourceFields?.data?.grouped_by_step?.map((stepGroup) => (
                        <optgroup key={stepGroup.step_number} label={`${stepGroup.phase_name} - Step ${stepGroup.step_number}: ${stepGroup.step_title}`}>
                          {stepGroup.fields.map((f) => (
                            <option
                              key={f.field_id}
                              value={`${f.step_number}|${f.variable_name}|${f.phase_name}`}
                            >
                              {f.field_label} ({f.variable_name})
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                    {(errors.sourceStep || errors.sourceVariableName) && (
                      <p className="error-text">{errors.sourceStep || errors.sourceVariableName}</p>
                    )}
                    <small className="form-text">Select which field from a previous step to display here</small>
                  </div>

                  <div className="form-group">
                    <label>Placeholder Text</label>
                    <input
                      type="text"
                      value={placeholder}
                      onChange={(e) => setPlaceholder(e.target.value)}
                      placeholder="Text to show when no value exists (optional)"
                    />
                    <small className="form-text">Shown when the user hasn't filled in the source field yet</small>
                  </div>

                  {sourceStep && sourceVariableName && (
                    <div className="source-field-preview">
                      <strong>Will display:</strong> Step {sourceStep} → {sourceVariableName}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="form-group">
                    <label>Variable Name <span className="required">*</span></label>
                    <input
                      type="text"
                      value={variableName}
                      onChange={(e) => setVariableName(e.target.value)}
                      placeholder="e.g., calc_monthly_cost"
                      className={errors.variableName ? 'is-invalid' : ''}
                    />
                    {errors.variableName && <p className="error-text">{errors.variableName}</p>}
                    <small className="form-text">Used to reference this calculated value in other formulas</small>
                  </div>

                  <div className="form-group">
                    <label>Formula <span className="required">*</span></label>
                    <input
                      type="text"
                      value={formulaExpression}
                      onChange={(e) => setFormulaExpression(e.target.value)}
                      placeholder="e.g., total_weekly_hours * avg_hourly_rate * 52 / 12"
                      className={errors.formula ? 'is-invalid' : ''}
                    />
                    {errors.formula && <p className="error-text">{errors.formula}</p>}
                    <small className="form-text">
                      Use variable names from this step, or <code>stepN.variable_name</code> for cross-step references.
                      Supports: <code>+ - * / ( )</code>
                    </small>
                  </div>

                  <div className="form-group">
                    <label>Output Format</label>
                    <select
                      value={formulaFormat}
                      onChange={(e) => setFormulaFormat(e.target.value)}
                    >
                      <option value="number">Number (e.g. 1,234)</option>
                      <option value="currency">Currency (e.g. $1,234.00)</option>
                      <option value="percentage">Percentage (e.g. 12.5%)</option>
                    </select>
                  </div>
                </>
              )}
            </div>
          );
        }
        // static-content falls through to default/inner switch

      default:
        // Handle new component types
        switch (componentType) {
          case 'text-input':
          case 'currency-input':
          case 'date-input':
          case 'email-input':
          case 'url-input':
          case 'number-input':
            return (
              <div className="field-type-options">
                <div className="form-group">
                  <label>Variable Name</label>
                  <input
                    type="text"
                    value={variableName}
                    onChange={(e) => setVariableName(e.target.value)}
                    placeholder="e.g., user_input (optional, for referencing later)"
                  />
                  <small className="form-text">Used to reference this field's value in later steps</small>
                </div>
                <div className="form-group">
                  <label>Placeholder Text</label>
                  <input
                    type="text"
                    value={placeholder}
                    onChange={(e) => setPlaceholder(e.target.value)}
                    placeholder="Enter placeholder text (optional)"
                  />
                </div>
                {renderTextFormatConfiguration()}
              </div>
            );

          case 'checkbox-group':
            return (
              <div className="field-type-options">
                <div className="form-group">
                  <label>Variable Name</label>
                  <input
                    type="text"
                    value={variableName}
                    onChange={(e) => setVariableName(e.target.value)}
                    placeholder="e.g., selected_options (optional)"
                  />
                </div>
                {renderCheckboxConfiguration()}
                {renderOptionsEditor()}
              </div>
            );

          case 'repeater':
            return (
              <div className="field-type-options">
                <div className="form-group">
                  <label>Variable Name <span className="required">*</span></label>
                  <input
                    type="text"
                    value={variableName}
                    onChange={(e) => setVariableName(e.target.value)}
                    placeholder="e.g., competitors"
                  />
                </div>
                {renderRepeaterFieldBuilder()}
              </div>
            );

          case 'file-upload':
            return (
              <div className="field-type-options">
                <div className="form-group">
                  <label>Variable Name</label>
                  <input
                    type="text"
                    value={variableName}
                    onChange={(e) => setVariableName(e.target.value)}
                    placeholder="e.g., uploaded_document"
                  />
                </div>
                {renderFileUploadConfiguration()}
              </div>
            );

          case 'button':
          case 'download-button':
          case 'link-button':
          case 'copy-block':
            return (
              <div className="field-type-options">
                {renderButtonConfiguration()}
              </div>
            );

          case 'list-view':
            return (
              <div className="field-type-options">
                <div className="form-group">
                  <label>Variable Name</label>
                  <input
                    type="text"
                    value={variableName}
                    onChange={(e) => setVariableName(e.target.value)}
                    placeholder="e.g., shortlisted_sites"
                  />
                </div>
                {renderListViewConfiguration()}
              </div>
            );

          case 'connect-list':
            return (
              <div className="field-type-options">
                <div className="form-group">
                  <label>Variable Name</label>
                  <input
                    type="text"
                    value={variableName}
                    onChange={(e) => setVariableName(e.target.value)}
                    placeholder="e.g., partner_connections"
                  />
                </div>
                {renderConnectListConfiguration()}
              </div>
            );

          case 'display-only':
          case 'read-only-text':
            return (
              <div className="field-type-options">
                <div className="alert alert-info">
                  <i className="fas fa-info-circle"></i> Display-only fields show values from previous steps, or calculated results.
                </div>

                {/* Formula / Source toggle */}
                <div className="form-group">
                  <label>Display Mode</label>
                  <div className="btn-group w-100" role="group">
                    <button
                      type="button"
                      className={`btn btn-sm ${!isFormulaMode ? 'btn-primary' : 'btn-outline-secondary'}`}
                      onClick={() => { setIsFormulaMode(false); setErrors({}); }}
                    >
                      Source Field
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm ${isFormulaMode ? 'btn-primary' : 'btn-outline-secondary'}`}
                      onClick={() => { setIsFormulaMode(true); setErrors({}); }}
                    >
                      Calculated / Formula
                    </button>
                  </div>
                </div>

                {!isFormulaMode ? (
                  <>
                    <div className="form-group">
                      <label>Source Field <span className="required">*</span></label>
                      <select
                        value={sourceStep && sourceVariableName ? `${sourceStep}|${sourceVariableName}` : ''}
                        onChange={handleSourceFieldSelect}
                        className={errors.sourceStep || errors.sourceVariableName ? 'is-invalid' : ''}
                      >
                        <option value="">Select a field from a previous step...</option>
                        {availableSourceFields?.data?.grouped_by_step?.map((stepGroup) => (
                          <optgroup key={stepGroup.step_number} label={`Step ${stepGroup.step_number}: ${stepGroup.step_title}`}>
                            {stepGroup.fields.map((f) => (
                              <option
                                key={f.field_id}
                                value={`${f.step_number}|${f.variable_name}`}
                              >
                                {f.field_label} ({f.variable_name})
                              </option>
                            ))}
                          </optgroup>
                        ))}
                      </select>
                      {(errors.sourceStep || errors.sourceVariableName) && (
                        <p className="error-text">{errors.sourceStep || errors.sourceVariableName}</p>
                      )}
                    </div>

                    <div className="form-group">
                      <label>Placeholder Text</label>
                      <input
                        type="text"
                        value={placeholder}
                        onChange={(e) => setPlaceholder(e.target.value)}
                        placeholder="Text to show when no value exists (optional)"
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="form-group">
                      <label>Variable Name <span className="required">*</span></label>
                      <input
                        type="text"
                        value={variableName}
                        onChange={(e) => setVariableName(e.target.value)}
                        placeholder="e.g., calc_monthly_cost"
                        className={errors.variableName ? 'is-invalid' : ''}
                      />
                      {errors.variableName && <p className="error-text">{errors.variableName}</p>}
                      <small className="form-text">Used to reference this calculated value in other formulas</small>
                    </div>

                    <div className="form-group">
                      <label>Formula <span className="required">*</span></label>
                      <input
                        type="text"
                        value={formulaExpression}
                        onChange={(e) => setFormulaExpression(e.target.value)}
                        placeholder="e.g., total_weekly_hours * avg_hourly_rate * 52 / 12"
                        className={errors.formula ? 'is-invalid' : ''}
                      />
                      {errors.formula && <p className="error-text">{errors.formula}</p>}
                      <small className="form-text">
                        Use variable names from this step, or <code>stepN.variable_name</code> for cross-step references.
                        Supports: <code>+ - * / ( )</code>
                      </small>
                    </div>

                    <div className="form-group">
                      <label>Output Format</label>
                      <select
                        value={formulaFormat}
                        onChange={(e) => setFormulaFormat(e.target.value)}
                      >
                        <option value="number">Number (e.g. 1,234)</option>
                        <option value="currency">Currency (e.g. $1,234.00)</option>
                        <option value="percentage">Percentage (e.g. 12.5%)</option>
                      </select>
                    </div>
                  </>
                )}
              </div>
            );

          case 'static-content':
            return (
              <div className="field-type-options">
                <div className="alert alert-info">
                  <i className="fas fa-info-circle"></i> Static Content displays instructional text to the user. No user input is collected.
                </div>
                <div className="form-group">
                  <label>Content <span className="required">*</span></label>
                  <textarea
                    value={componentMetadata.content || ''}
                    onChange={(e) => setComponentMetadata({ ...componentMetadata, content: e.target.value })}
                    placeholder="Enter the instructional text shown to the user. Supports **bold**, bullet lists (- item), numbered lists (1. item), and tables."
                    rows={8}
                  />
                  <small className="form-text">
                    Supports basic markdown: <code>**bold**</code>, <code>- bullet</code>, <code>1. numbered</code>
                  </small>
                </div>
                <div className="form-group">
                  <label>Display Style</label>
                  <select
                    value={componentMetadata.style || 'info'}
                    onChange={(e) => setComponentMetadata({ ...componentMetadata, style: e.target.value })}
                  >
                    <option value="info">Info (blue)</option>
                    <option value="tip">Tip (green)</option>
                    <option value="warning">Warning (amber)</option>
                    <option value="plain">Plain (grey)</option>
                  </select>
                  <small className="form-text">Choose the visual style for this instruction block</small>
                </div>
              </div>
            );

          default:
            return null;
        }
    }
  };

  const renderOptionsEditor = () => {
    return (
      <>
        <div className="options-header">
          <label>Options <span className="required">*</span></label>
          <button type="button" className="btn-add-option" onClick={handleAddOption}>
            <i className="fas fa-plus"></i> Add Option
          </button>
        </div>
        {errors.options && <p className="error-text">{errors.options}</p>}
        <div className="options-list">
          {options.map((option, index) => (
            <div key={index} className="option-item">
              <div className="option-header">
                <span className="option-number">Option {index + 1}</span>
                {options.length > 1 && (
                  <button
                    type="button"
                    className="btn-remove-option"
                    onClick={() => handleRemoveOption(index)}
                  >
                    <i className="fas fa-times"></i>
                  </button>
                )}
              </div>
              <div className="form-group">
                <label>Value <span className="required">*</span></label>
                <input
                  type="text"
                  value={option.value || ''}
                  onChange={(e) => handleOptionChange(index, 'value', e.target.value)}
                  placeholder="e.g., option_1"
                />
              </div>
              <div className="form-group">
                <label>Label <span className="required">*</span></label>
                <input
                  type="text"
                  value={option.label || ''}
                  onChange={(e) => handleOptionChange(index, 'label', e.target.value)}
                  placeholder="e.g., Option 1"
                />
              </div>
            </div>
          ))}
        </div>
      </>
    );
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Body className="field-configurator-modal-body">
        <div className="field-configurator-container">
          <div className="field-configurator-header">
            <h5>{field ? 'Edit Field' : 'Add New Field'}</h5>
            <button className="btn-close-modal" onClick={onHide}>
              <i className="fas fa-times"></i>
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="field-configurator-content">
              {/* Field Type Selection - Show grouped field types if available */}
              {fieldTypesData?.data?.grouped ? (
                <>
                  <div className="form-group">
                    <label>Field Category <span className="required">*</span></label>
                    <div className="field-type-categories">
                      {Object.keys(fieldTypesData.data.grouped).map((category) => (
                        <button
                          key={category}
                          type="button"
                          className={`category-btn ${
                            fieldTypesData.data.grouped[category].some(t => t.component_type === componentType)
                              ? 'active'
                              : ''
                          }`}
                          onClick={() => {
                            const firstType = fieldTypesData.data.grouped[category][0];
                            setComponentType(firstType.component_type);
                            setFieldType(firstType.base_type);
                            setComponentMetadata(getDefaultMetadata(firstType.component_type));
                          }}
                        >
                          {category}
                        </button>
                      ))}
                    </div>
                  </div>
                  {renderComponentTypeSelector()}
                </>
              ) : (
                // Legacy field type selector
                <div className="form-group">
                  <label>Field Type <span className="required">*</span></label>
                  <div className="field-type-selector">
                    <button
                      type="button"
                      className={`field-type-btn ${fieldType === 'text' ? 'active' : ''}`}
                      onClick={() => handleFieldTypeChange('text')}
                    >
                      <i className="fas fa-font"></i>
                      <span>Text</span>
                    </button>
                    <button
                      type="button"
                      className={`field-type-btn ${fieldType === 'textarea' ? 'active' : ''}`}
                      onClick={() => handleFieldTypeChange('textarea')}
                    >
                      <i className="fas fa-align-left"></i>
                      <span>Text Area</span>
                    </button>
                    <button
                      type="button"
                      className={`field-type-btn ${fieldType === 'checkbox' ? 'active' : ''}`}
                      onClick={() => handleFieldTypeChange('checkbox')}
                    >
                      <i className="fas fa-check-square"></i>
                      <span>Checkbox</span>
                    </button>
                    <button
                      type="button"
                      className={`field-type-btn ${fieldType === 'radio' ? 'active' : ''}`}
                      onClick={() => handleFieldTypeChange('radio')}
                    >
                      <i className="fas fa-dot-circle"></i>
                      <span>Radio</span>
                    </button>
                    <button
                      type="button"
                      className={`field-type-btn ${fieldType === 'dropdown' ? 'active' : ''}`}
                      onClick={() => handleFieldTypeChange('dropdown')}
                    >
                      <i className="fas fa-caret-square-down"></i>
                      <span>Dropdown</span>
                    </button>
                    <button
                      type="button"
                      className={`field-type-btn ${fieldType === 'display' ? 'active' : ''}`}
                      onClick={() => handleFieldTypeChange('display')}
                    >
                      <i className="fas fa-eye"></i>
                      <span>Display Only</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Field Label */}
              <div className="form-group">
                <label>
                  Field Label {componentType !== 'static-content' && <span className="required">*</span>}
                  {componentType === 'static-content' && <span className="text-muted"> (optional heading)</span>}
                </label>
                <textarea
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder={componentType === 'static-content' ? 'Optional heading shown above the content block' : 'Enter the question or label for this field'}
                  rows={2}
                  className={errors.label ? 'is-invalid' : ''}
                />
                {errors.label && <p className="error-text">{errors.label}</p>}
              </div>

              {/* Help Text */}
              {fieldType !== 'display' && (
                <div className="form-group">
                  <label>Help Text</label>
                  <input
                    type="text"
                    value={helpText}
                    onChange={(e) => setHelpText(e.target.value)}
                    placeholder="Optional hint shown below the field label (e.g. on-costs explanation)"
                  />
                  <small className="form-text">Displayed as a subtitle beneath the field label on the user-facing form</small>
                </div>
              )}

              {/* Required Checkbox */}
              {fieldType !== 'display' && (
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={isRequired}
                      onChange={(e) => setIsRequired(e.target.checked)}
                    />
                    <span>This field is required</span>
                  </label>
                </div>
              )}

              {/* Field Type Specific Options */}
              {renderFieldTypeOptions()}
            </div>

            <div className="field-configurator-footer">
              <button type="button" className="btn-outline" onClick={onHide} disabled={isLoading}>
                Cancel
              </button>
              <button type="submit" className="btn-primary" disabled={isLoading}>
                {isLoading ? 'Saving...' : field ? 'Update Field' : 'Add Field'}
              </button>
            </div>
          </form>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default FieldConfigurator;
